import { Link } from "@tanstack/solid-router";
import { For, Show, type JSX, type ParentProps } from "solid-js";

import { GlobalSearch } from "~/components/GlobalSearch";
import { Logo } from "~/components/Logo";
import { NotificationsBell } from "~/components/NotificationsBell";
import { SuspensionBanner } from "~/components/SuspensionBanner";
import { ThemeToggle } from "~/components/ThemeToggle";
import { useCustomObjects, useDashboard, useMyWorkspaces } from "~/lib/hooks";

type Props = ParentProps<{
  wsKey: string;
  pageTitle?: string;
  rightSlot?: JSX.Element;
}>;

export function AppShell(props: Props) {
  // Drives the badge on the Tasks nav link. staleTime keeps this cheap;
  // it's only re-fetched once per ~30s even as the user navigates.
  const dashboard = useDashboard(() => props.wsKey);
  const overdueCount = () => {
    const t = dashboard.data?.tasks;
    if (!t) return 0;
    return t.my_overdue + t.my_due_today;
  };
  // Dynamic "Custom" nav section — lists each active workspace-declared
  // custom object so admins can click straight into the records list.
  const customObjects = useCustomObjects(() => props.wsKey);
  const activeCustomObjects = () => (customObjects.data?.items ?? []).filter((o) => !o.archived_at);
  // Partner-admin link visibility — gate on the current workspace's
  // membership row. Cached 5min; the same query backs the WorkspacesPage.
  const myWorkspaces = useMyWorkspaces();
  const isPartnerAdminHere = () =>
    myWorkspaces.data?.items.some((m) => m.workspace_key === props.wsKey && m.is_partner_admin) ??
    false;
  return (
    <div class="min-h-screen bg-base-200">
      <SuspensionBanner wsKey={props.wsKey} />
      <div class="drawer lg:drawer-open">
        <input id="sidebar-toggle" type="checkbox" class="drawer-toggle" />
        <div class="drawer-content flex flex-col">
          <div class="navbar bg-base-100 border-b border-base-300 px-3 sm:px-4">
            <div class="flex-none lg:hidden">
              <label
                for="sidebar-toggle"
                class="btn btn-square btn-ghost"
                aria-label="Open navigation menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  class="inline-block w-6 h-6 stroke-current"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </label>
            </div>
            <h1 class="flex-1 text-base sm:text-lg font-semibold truncate">
              {props.pageTitle ?? "ScaiCRM"}
            </h1>
            <div class="flex-none flex items-center gap-2">
              <GlobalSearch wsKey={() => props.wsKey} />
              <NotificationsBell wsKey={() => props.wsKey} />
              <ThemeToggle />
              {props.rightSlot}
            </div>
          </div>
          <main id="main" class="p-4 sm:p-6 grow">
            {props.children}
          </main>
        </div>
        <div class="drawer-side">
          <label for="sidebar-toggle" class="drawer-overlay" aria-label="Close navigation menu" />
          <aside class="bg-base-100 w-64 min-h-full border-r border-base-300">
            <div class="p-4">
              <Logo width={160} height={38} class="block" />
            </div>
            <div class="px-4 text-xs uppercase tracking-wider opacity-50 mb-1">Workspace</div>
            <div class="px-4 mb-4 truncate font-medium" title={props.wsKey}>
              {props.wsKey}
            </div>
            <nav
              class="menu p-2 text-base-content"
              aria-label="Main navigation"
              onClick={(e) => {
                // Mobile drawer auto-close: when a link inside the nav is
                // tapped, uncheck the toggle so the drawer slides away.
                // No-op on desktop (`lg:drawer-open` keeps it visible
                // regardless of the toggle state).
                if ((e.target as HTMLElement).closest("a")) {
                  const toggle = document.getElementById(
                    "sidebar-toggle",
                  ) as HTMLInputElement | null;
                  if (toggle) toggle.checked = false;
                }
              }}
            >
              <li>
                {/* Home + Settings need exact matching — otherwise their child
                    routes (/accounts, /settings/pipelines, …) would keep them
                    "active" forever. Section links use the default prefix
                    match so detail pages still highlight their parent. */}
                <Link
                  to="/workspaces/$wsKey"
                  params={{ wsKey: props.wsKey }}
                  activeOptions={{ exact: true }}
                  activeProps={{ class: "active" }}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/workspaces/$wsKey/accounts"
                  params={{ wsKey: props.wsKey }}
                  activeProps={{ class: "active" }}
                >
                  Accounts
                </Link>
              </li>
              <li>
                <Link
                  to="/workspaces/$wsKey/contacts"
                  params={{ wsKey: props.wsKey }}
                  activeProps={{ class: "active" }}
                >
                  Contacts
                </Link>
              </li>
              <li>
                <Link
                  to="/workspaces/$wsKey/deals"
                  params={{ wsKey: props.wsKey }}
                  activeProps={{ class: "active" }}
                >
                  Deals
                </Link>
              </li>
              <li>
                <Link
                  to="/workspaces/$wsKey/products"
                  params={{ wsKey: props.wsKey }}
                  activeProps={{ class: "active" }}
                >
                  Products
                </Link>
              </li>
              <Show when={activeCustomObjects().length > 0}>
                <li class="menu-title text-xs opacity-60 mt-2">
                  <span>Custom</span>
                </li>
                <For each={activeCustomObjects()}>
                  {(co) => (
                    <li>
                      <Link
                        to="/workspaces/$wsKey/custom/$objectKey"
                        params={{ wsKey: props.wsKey, objectKey: co.key }}
                        activeProps={{ class: "active" }}
                      >
                        {co.label_plural}
                      </Link>
                    </li>
                  )}
                </For>
              </Show>
              <li>
                <Link
                  to="/workspaces/$wsKey/tasks"
                  params={{ wsKey: props.wsKey }}
                  class="flex items-center justify-between"
                  activeProps={{ class: "active" }}
                >
                  <span>Tasks</span>
                  <Show when={overdueCount() > 0}>
                    <span
                      class={`badge badge-sm ${
                        (dashboard.data?.tasks.my_overdue ?? 0) > 0
                          ? "badge-error"
                          : "badge-warning"
                      }`}
                      aria-label={`${overdueCount()} tasks need attention`}
                    >
                      {overdueCount()}
                    </span>
                  </Show>
                </Link>
              </li>
              <li>
                <Link
                  to="/workspaces/$wsKey/reports"
                  params={{ wsKey: props.wsKey }}
                  activeProps={{ class: "active" }}
                >
                  Reports
                </Link>
              </li>
              <li class="mt-2">
                <Link
                  to="/workspaces/$wsKey/settings"
                  params={{ wsKey: props.wsKey }}
                  activeOptions={{ exact: true }}
                  activeProps={{ class: "active" }}
                >
                  Settings
                </Link>
              </li>
              <li>
                <Link
                  to="/workspaces/$wsKey/settings/pipelines"
                  params={{ wsKey: props.wsKey }}
                  activeProps={{ class: "active" }}
                >
                  Pipelines
                </Link>
              </li>
              <Show when={isPartnerAdminHere()}>
                <li class="mt-2">
                  <Link
                    to="/workspaces/$wsKey/partner-admin"
                    params={{ wsKey: props.wsKey }}
                    activeProps={{ class: "active" }}
                  >
                    Partner admin
                  </Link>
                </li>
              </Show>
            </nav>
          </aside>
        </div>
      </div>
    </div>
  );
}
